import React, { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getFlowAssignmentsByUserPublic,
  getTenantPagingMyZones,
  getTenantSipPagingMyZones,
  getMyImageDisplayActions,
  listCollaborationWorkspaces,
  listContactLists,
} from '../../../api/backendApi';
import { flowsApi } from '../../../services/flowsApiService';
import { actionApi } from '../../../services/eventSystemApiServiceFixed';
import { actionTypeLabel, uiTypeFor } from '../../Admin/actionListFilters';
import {
  ButtonEditor,
  HOME_ACTION_ACTION,
  HOME_ACTION_FLOW,
  HOME_ACTION_PAGING,
  HOME_ACTION_SIP_PAGING,
  HOME_ACTION_IMAGE_DISPLAY,
  HOME_ACTION_COLLABORATION,
  HOME_ACTION_CONTACT_LIST,
  HOME_ACTION_PHONE,
  HOME_ACTION_REMINDERS,
  HOME_ACTION_EMAIL_REM,
  HOME_ACTION_ASK_SB,
  HOME_ACTION_OPEN_SCREEN_SAVER,
  HOME_ACTION_NONE,
  canSaveHomeLauncherButton,
  buildHomeLauncherSavePayload,
} from '../../../shared/buttonEditor';
import { uploadLauncherIcon } from '../../../services/homeLauncher/uploadLauncherIcon';
import { EDITOR_MENU_PROPS } from '../../../shared/buttonEditor/editorLayers';
import { isPhoneEnabledForUser, PHONE_ADMIN_ROLES } from '../../../utils/phoneEntitlement';
import { isRemindersEnabledForUser, REMINDERS_ADMIN_ROLES } from '../../../utils/remindersEntitlement';
import { isEmailRemEnabledForUser, EMAIL_REM_ADMIN_ROLES } from '../../../utils/emailRemEntitlement';
import { getAskSbAccess } from '../../../services/askSb/askSbApi';

const ADMIN_FLOW_ROLES = new Set(['admin', 'superadmin', 'tenant_admin']);

// These selects render inside the editor dialog (z-index 1600). Without a higher
// menu layer, long lists such as microphones open behind the dialog.
const NESTED_SELECT_MENU_PROPS = {
  ...EDITOR_MENU_PROPS,
  slotProps: {
    paper: {
      sx: {
        maxHeight: 320,
        maxWidth: 420,
      },
    },
  },
};

const HOME_BUTTON_EXCLUDED_ACTION_TYPES = new Set(['wait', 'delay', 'image_display']);

function mapTenantActionOptions(actions) {
  return (Array.isArray(actions) ? actions : [])
    .filter((action) => {
      if (!action?.id) return false;
      const type = String(action.type || '').toLowerCase();
      return !HOME_BUTTON_EXCLUDED_ACTION_TYPES.has(type);
    })
    .map((action) => ({
      id: action.id,
      name: action.label || action.name || action.id,
      typeLabel: actionTypeLabel(uiTypeFor(action)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

function ActionPickTable({ actions, selectedId, onSelect, loading, disabled }) {
  const [sort, setSort] = useState({ key: 'type', dir: 'asc' });
  const rows = useMemo(() => {
    const list = [...(Array.isArray(actions) ? actions : [])];
    const dir = sort.dir === 'desc' ? -1 : 1;
    list.sort((a, b) => {
      const left = sort.key === 'label' ? String(a.name || '') : String(a.typeLabel || '');
      const right = sort.key === 'label' ? String(b.name || '') : String(b.typeLabel || '');
      const cmp = left.localeCompare(right, undefined, { sensitivity: 'base' });
      if (cmp !== 0) return cmp * dir;
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
    });
    return list;
  }, [actions, sort]);

  const toggleSort = (key) => {
    setSort((prev) => (
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    ));
  };

  if (loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption">Loading actions…</Typography>
      </Stack>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        No actions found. Create one under Event System → Actions. For Image Display, use
        the Image Display button type.
      </Typography>
    );
  }

  return (
    <Table size="small" stickyHeader data-testid="home-button-actions-table">
      <TableHead>
        <TableRow>
          <TableCell
            sortDirection={sort.key === 'type' ? sort.dir : false}
            onClick={() => toggleSort('type')}
            sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700 }}
          >
            <TableSortLabel active={sort.key === 'type'} direction={sort.key === 'type' ? sort.dir : 'asc'}>
              Type
            </TableSortLabel>
          </TableCell>
          <TableCell
            sortDirection={sort.key === 'label' ? sort.dir : false}
            onClick={() => toggleSort('label')}
            sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700 }}
          >
            <TableSortLabel active={sort.key === 'label'} direction={sort.key === 'label' ? sort.dir : 'asc'}>
              Label
            </TableSortLabel>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((action) => {
          const selected = action.id === selectedId;
          return (
            <TableRow
              key={action.id}
              hover
              selected={selected}
              onClick={() => {
                if (!disabled) onSelect(action.id);
              }}
              sx={{ cursor: disabled ? 'default' : 'pointer' }}
            >
              <TableCell>{action.typeLabel}</TableCell>
              <TableCell>{action.name}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function mapFlowOptions(flows) {
  return (Array.isArray(flows) ? flows : [])
    .map((f) => {
      const canonicalId = f.flowId || f.id;
      return {
        id: canonicalId,
        name: f.flowName || f.name || canonicalId,
        rawId: f.id,
        rawFlowId: f.flowId,
      };
    })
    .filter((o) => !!o.id);
}

/**
 * HomeLauncher adapter around shared ButtonEditor.
 * Loads Home-specific options (flows, zones, mics, image actions)
 * and preserves the existing save payload + validation behavior.
 */
export default function ButtonAdminModal({
  open,
  icon,
  onClose,
  onSave,
  onDelete,
  imageScope = 'personal',
}) {
  const { currentUser, tenantId, userData, role } = useAuth();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [pagingZones, setPagingZones] = useState([]);
  const [pagingLoading, setPagingLoading] = useState(false);
  const [audioDevices, setAudioDevices] = useState([]);
  const [sipPagingZones, setSipPagingZones] = useState([]);
  const [sipPagingLoading, setSipPagingLoading] = useState(false);
  const [imageDisplayActions, setImageDisplayActions] = useState([]);
  const [imageDisplayLoading, setImageDisplayLoading] = useState(false);
  const [collaborationWorkspaces, setCollaborationWorkspaces] = useState([]);
  const [collaborationLoading, setCollaborationLoading] = useState(false);
  const [contactLists, setContactLists] = useState([]);
  const [contactListsLoading, setContactListsLoading] = useState(false);
  const [tenantActions, setTenantActions] = useState([]);
  const [tenantActionsLoading, setTenantActionsLoading] = useState(false);
  const [canAssignAskSb, setCanAssignAskSb] = useState(false);

  // Track current flowId in editor only for UUID→canonical remapping side effect
  const [flowIdForRemap, setFlowIdForRemap] = useState(icon?.flowId || '');

  useEffect(() => {
    setFlowIdForRemap(icon?.flowId || '');
  }, [icon]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!open || !currentUser?.uid || !tenantId) return;
      setLoading(true);
      try {
        let assignedIds = [];
        let flows = [];

        try {
          const resp = await getFlowAssignmentsByUserPublic(currentUser.uid, tenantId, true);
          if (resp && resp.data) {
            assignedIds = Array.isArray(resp.data.flowIds)
              ? resp.data.flowIds
              : Array.isArray(resp.data)
                ? resp.data
                : [];
            flows = Array.isArray(resp.data.flows) ? resp.data.flows : [];
          } else if (Array.isArray(resp)) {
            assignedIds = resp;
          }
        } catch (assignErr) {
          console.warn('[ButtonAdminModal] Flow assignments API failed; using fallback', assignErr);
        }

        const isAdminEditor = ADMIN_FLOW_ROLES.has(role);
        if ((!flows.length && assignedIds.length) || (!flows.length && !assignedIds.length && isAdminEditor)) {
          try {
            const all = await flowsApi.getAll(tenantId);
            const allFlows = Array.isArray(all) ? all : all?.data || [];
            if (assignedIds.length) {
              flows = allFlows.filter(
                (f) => assignedIds.includes(f.flowId) || assignedIds.includes(f.id)
              );
            } else if (isAdminEditor) {
              flows = allFlows;
            }
          } catch (fallbackErr) {
            console.warn('[ButtonAdminModal] Flow list fallback failed', fallbackErr);
          }
        }

        const opts = mapFlowOptions(flows);
        if (active) setOptions(opts);

        const uuidLike =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (active && flowIdForRemap && uuidLike.test(String(flowIdForRemap))) {
          const match = opts.find((o) => o.rawId === flowIdForRemap && o.rawFlowId);
          if (match?.rawFlowId) setFlowIdForRemap(match.rawFlowId);
        }
      } catch (err) {
        console.error('[ButtonAdminModal] Failed to load flow options', err);
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [open, currentUser?.uid, tenantId, flowIdForRemap, role]);

  useEffect(() => {
    let active = true;
    const loadZonesAndDevices = async () => {
      if (!open || !tenantId) return;
      setPagingLoading(true);
      setSipPagingLoading(true);
      try {
        const zones = await getTenantPagingMyZones(tenantId);
        if (active) setPagingZones(zones);
      } catch (e) {
        console.warn('[ButtonAdminModal] Failed to load paging zones', e);
      } finally {
        if (active) setPagingLoading(false);
      }

      try {
        const sipZones = await getTenantSipPagingMyZones(tenantId);
        if (active) setSipPagingZones(sipZones);
      } catch (e) {
        console.warn('[ButtonAdminModal] Failed to load SIP paging zones', e);
      } finally {
        if (active) setSipPagingLoading(false);
      }

      setImageDisplayLoading(true);
      try {
        const imgActions = await getMyImageDisplayActions(tenantId);
        if (active) setImageDisplayActions(imgActions);
      } catch (e) {
        console.warn('[ButtonAdminModal] Failed to load image display actions', e);
      } finally {
        if (active) setImageDisplayLoading(false);
      }

      try {
        if (navigator.mediaDevices?.enumerateDevices) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const audioInputs = devices.filter((d) => d.kind === 'audioinput');
          if (active) setAudioDevices(audioInputs);
        }
      } catch (e) {
        console.warn('[ButtonAdminModal] Failed to enumerate audio devices', e);
      }

      setCollaborationLoading(true);
      try {
        const workspaces = await listCollaborationWorkspaces(tenantId);
        if (active) setCollaborationWorkspaces(Array.isArray(workspaces) ? workspaces : []);
      } catch (e) {
        console.warn('[ButtonAdminModal] Failed to load collaboration workspaces', e);
        if (active) setCollaborationWorkspaces([]);
      } finally {
        if (active) setCollaborationLoading(false);
      }

      setContactListsLoading(true);
      try {
        const lists = await listContactLists(tenantId);
        if (active) setContactLists(Array.isArray(lists) ? lists : []);
      } catch (e) {
        console.warn('[ButtonAdminModal] Failed to load contact lists', e);
        if (active) setContactLists([]);
      } finally {
        if (active) setContactListsLoading(false);
      }

      setTenantActionsLoading(true);
      try {
        const resp = await actionApi.getAll(tenantId);
        const raw = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        if (active) setTenantActions(mapTenantActionOptions(raw));
      } catch (e) {
        console.warn('[ButtonAdminModal] Failed to load actions', e);
        if (active) setTenantActions([]);
      } finally {
        if (active) setTenantActionsLoading(false);
      }
    };
    loadZonesAndDevices();
    return () => {
      active = false;
    };
  }, [open, tenantId]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    getAskSbAccess()
      .then((access) => {
        if (active) setCanAssignAskSb(access?.canConsume === true);
      })
      .catch(() => {
        if (active) setCanAssignAskSb(false);
      });
    return () => {
      active = false;
    };
  }, [open]);

  const isSettings = icon?.id === 'settings';
  const canAssignPhone = isPhoneEnabledForUser(userData) || PHONE_ADMIN_ROLES.has(role);
  const canAssignReminders = isRemindersEnabledForUser(userData) || REMINDERS_ADMIN_ROLES.has(role);
  const canAssignEmailRem = isEmailRemEnabledForUser(userData) || EMAIL_REM_ADMIN_ROLES.has(role);

  const actions = useMemo(
    () => [
      {
        value: HOME_ACTION_FLOW,
        label: 'Trigger Flow',
        renderConfig: ({ values, setValues }) => (
          <FormControl fullWidth disabled={loading || isSettings}>
            <InputLabel id="flow-select-label">Assigned Flow</InputLabel>
            <Select
              labelId="flow-select-label"
              label="Assigned Flow"
              MenuProps={NESTED_SELECT_MENU_PROPS}
              value={values.flowId || ''}
              onChange={(e) => {
                const next = e.target.value;
                setValues({ flowId: next });
                setFlowIdForRemap(next);
              }}
              renderValue={(val) => {
                if (!val) return 'None';
                const found = options.find((o) => o.id === val);
                return found ? found.name : val;
              }}
            >
              <MenuItem value="">None</MenuItem>
              {options.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
            {loading && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading permitted flows…</Typography>
              </Stack>
            )}
          </FormControl>
        ),
      },
      {
        value: HOME_ACTION_ACTION,
        label: 'Run Action',
        renderConfig: ({ values, setValues }) => (
          <ActionPickTable
            actions={tenantActions}
            selectedId={values.actionId || ''}
            loading={tenantActionsLoading}
            disabled={isSettings}
            onSelect={(actionId) => setValues({ actionId })}
          />
        ),
      },
      {
        value: HOME_ACTION_PAGING,
        label: 'RTP Paging Zone',
        renderConfig: ({ values, setValues }) => (
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="paging-zone-label">RTP Paging Zone</InputLabel>
              <Select
                labelId="paging-zone-label"
                label="RTP Paging Zone"
                MenuProps={NESTED_SELECT_MENU_PROPS}
                value={values.pagingZoneId || ''}
                onChange={(e) => setValues({ pagingZoneId: e.target.value })}
                renderValue={(val) => {
                  if (!val) return 'Select zone';
                  const found = pagingZones.find((z) => z.id === val);
                  return found ? found.name || found.id : val;
                }}
              >
                <MenuItem value="">Select zone</MenuItem>
                {pagingZones.map((z) => (
                  <MenuItem key={z.id} value={z.id}>
                    {z.name || z.id}
                    {z.description ? ` — ${z.description}` : ''}
                  </MenuItem>
                ))}
              </Select>
              {pagingLoading && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">Loading paging zones…</Typography>
                </Stack>
              )}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="mic-device-label">Microphone</InputLabel>
              <Select
                labelId="mic-device-label"
                label="Microphone"
                MenuProps={NESTED_SELECT_MENU_PROPS}
                value={values.micDeviceId || ''}
                onChange={(e) => setValues({ micDeviceId: e.target.value })}
                renderValue={(val) => {
                  if (!val) return 'Default microphone';
                  const found = audioDevices.find((d) => d.deviceId === val);
                  return found?.label || 'Selected microphone';
                }}
              >
                <MenuItem value="" sx={{ whiteSpace: 'normal' }}>Default microphone</MenuItem>
                {audioDevices.map((d) => (
                  <MenuItem
                    key={d.deviceId || d.label}
                    value={d.deviceId}
                    sx={{ whiteSpace: 'normal' }}
                  >
                    {d.label || 'Microphone'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        ),
      },
      {
        value: HOME_ACTION_SIP_PAGING,
        label: 'SIP Paging Zone',
        renderConfig: ({ values, setValues }) => (
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="sip-paging-zone-label">SIP Paging Zone</InputLabel>
              <Select
                labelId="sip-paging-zone-label"
                label="SIP Paging Zone"
                MenuProps={NESTED_SELECT_MENU_PROPS}
                value={values.sipPagingZoneId || ''}
                onChange={(e) => setValues({ sipPagingZoneId: e.target.value })}
                renderValue={(val) => {
                  if (!val) return 'Select zone';
                  const found = sipPagingZones.find((z) => z.id === val);
                  return found ? found.name || found.id : val;
                }}
              >
                <MenuItem value="">Select zone</MenuItem>
                {sipPagingZones.map((z) => (
                  <MenuItem key={z.id} value={z.id}>
                    {z.name || z.id}
                    {z.description ? ` — ${z.description}` : ''}
                  </MenuItem>
                ))}
              </Select>
              {sipPagingLoading && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">Loading SIP paging zones…</Typography>
                </Stack>
              )}
            </FormControl>
          </Stack>
        ),
      },
      {
        value: HOME_ACTION_IMAGE_DISPLAY,
        label: 'Image Display',
        renderConfig: ({ values, setValues }) => (
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="image-display-action-label">Image Display Action</InputLabel>
              <Select
                labelId="image-display-action-label"
                label="Image Display Action"
                MenuProps={NESTED_SELECT_MENU_PROPS}
                value={values.imageDisplayActionId || ''}
                onChange={(e) => setValues({ imageDisplayActionId: e.target.value })}
                renderValue={(val) => {
                  if (!val) return 'Select action';
                  const found = imageDisplayActions.find((a) => a.id === val);
                  return found ? found.label || found.id : val;
                }}
              >
                <MenuItem value="">Select action</MenuItem>
                {imageDisplayActions.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.label || a.id}
                  </MenuItem>
                ))}
              </Select>
              {imageDisplayLoading && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">Loading image display actions…</Typography>
                </Stack>
              )}
              {!imageDisplayLoading && imageDisplayActions.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  No image display actions available. Ask your admin to create an image
                  display action.
                </Typography>
              )}
            </FormControl>
          </Stack>
        ),
      },
      {
        value: HOME_ACTION_COLLABORATION,
        label: 'Collaboration Workspace',
        renderConfig: ({ values, setValues }) => (
          <FormControl fullWidth>
            <InputLabel id="collaboration-workspace-label">Collaboration Workspace</InputLabel>
            <Select
              labelId="collaboration-workspace-label"
              label="Collaboration Workspace"
              MenuProps={NESTED_SELECT_MENU_PROPS}
              value={values.collaborationWorkspaceId || ''}
              onChange={(e) => setValues({ collaborationWorkspaceId: e.target.value })}
              renderValue={(val) => {
                if (!val) return 'Select workspace';
                const found = collaborationWorkspaces.find((w) => w.id === val);
                return found ? found.name || found.id : val;
              }}
            >
              <MenuItem value="">Select workspace</MenuItem>
              {collaborationWorkspaces.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name || w.id}
                </MenuItem>
              ))}
            </Select>
            {collaborationLoading && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading collaboration workspaces…</Typography>
              </Stack>
            )}
            {!collaborationLoading && collaborationWorkspaces.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                No collaboration workspaces found. Create one under Admin → Collaboration.
              </Typography>
            )}
          </FormControl>
        ),
      },
      {
        value: HOME_ACTION_CONTACT_LIST,
        label: 'Contact List',
        renderConfig: ({ values, setValues }) => (
          <FormControl fullWidth>
            <InputLabel id="contact-list-label">Contact List</InputLabel>
            <Select
              labelId="contact-list-label"
              label="Contact List"
              MenuProps={NESTED_SELECT_MENU_PROPS}
              value={values.contactListId || ''}
              onChange={(e) => setValues({ contactListId: e.target.value })}
              renderValue={(val) => {
                if (!val) return 'Select contact list';
                const found = contactLists.find((l) => l.id === val);
                return found ? found.name || found.id : val;
              }}
            >
              <MenuItem value="">Select contact list</MenuItem>
              {contactLists.map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.name || l.id}
                </MenuItem>
              ))}
            </Select>
            {contactListsLoading && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading contact lists…</Typography>
              </Stack>
            )}
            {!contactListsLoading && contactLists.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                No contact lists found. Create one under Admin → Contact Lists.
              </Typography>
            )}
          </FormControl>
        ),
      },
      ...(canAssignPhone ? [{
        value: HOME_ACTION_PHONE,
        label: 'Phone',
      }] : []),
      ...(canAssignReminders ? [{
        value: HOME_ACTION_REMINDERS,
        label: 'Reminders & Timers',
      }] : []),
      ...(canAssignEmailRem ? [{
        value: HOME_ACTION_EMAIL_REM,
        label: 'Email Reminder',
      }] : []),
      ...(canAssignAskSb ? [{
        value: HOME_ACTION_ASK_SB,
        label: 'Ask Signal Bridges',
      }] : []),
      {
        value: HOME_ACTION_OPEN_SCREEN_SAVER,
        label: 'Open Screen Saver',
      },
      {
        value: HOME_ACTION_NONE,
        label: 'No Action',
      },
    ],
    [
      loading,
      isSettings,
      options,
      pagingZones,
      pagingLoading,
      audioDevices,
      sipPagingZones,
      sipPagingLoading,
      imageDisplayActions,
      imageDisplayLoading,
      collaborationWorkspaces,
      collaborationLoading,
      contactLists,
      contactListsLoading,
      tenantActions,
      tenantActionsLoading,
      canAssignPhone,
      canAssignReminders,
      canAssignEmailRem,
      canAssignAskSb,
    ]
  );

  const valuePatches = useMemo(() => {
    if (!flowIdForRemap || flowIdForRemap === (icon?.flowId || '')) {
      return null;
    }
    return { flowId: flowIdForRemap };
  }, [flowIdForRemap, icon?.flowId]);

  return (
    <ButtonEditor
      open={open}
      button={icon}
      title="Edit Button"
      actions={actions}
      actionSelectDisabled={isSettings}
      canDelete={!!icon && !isSettings}
      canSave={canSaveHomeLauncherButton}
      buildSavePayload={(values) =>
        buildHomeLauncherSavePayload(values, { fallbackLabel: icon?.label || '' })
      }
      valuePatches={valuePatches}
      customImage={tenantId ? {
        tenantId,
        scope: imageScope === 'shared' ? 'shared' : 'personal',
        upload: (file) => uploadLauncherIcon({
          file,
          tenantId,
          scope: imageScope === 'shared' ? 'shared' : 'personal',
        }),
      } : null}
      onClose={onClose}
      onSave={onSave}
      onDelete={onDelete}
    />
  );
}
